import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface NotificationsContextType {
  isNotificationsVisible: boolean;
  showNotificationsBubble: boolean;
  openNotifications: () => void;
  closeNotifications: () => void;
  toggleNotifications: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

const STORAGE_KEY = 'om-notifications-visibility';

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  // Load initial state from localStorage, default to visible
  const [isNotificationsVisible, setIsNotificationsVisible] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : true; // Default to visible
    } catch {
      return true; // Default to visible if localStorage is not available
    }
  });

  // Show bubble when notifications panel is closed
  const showNotificationsBubble = !isNotificationsVisible;

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(isNotificationsVisible));
    } catch {
      // Ignore localStorage errors
    }
  }, [isNotificationsVisible]);

  const openNotifications = () => {
    setIsNotificationsVisible(true);
  };

  const closeNotifications = () => {
    setIsNotificationsVisible(false);
  };

  const toggleNotifications = () => {
    setIsNotificationsVisible(!isNotificationsVisible);
  };

  const value: NotificationsContextType = {
    isNotificationsVisible,
    showNotificationsBubble,
    openNotifications,
    closeNotifications,
    toggleNotifications,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
